import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Select } from "../components/ui";
import AccountSelect from "../components/ui/AccountSelect";
import FilterChipInput from "../components/ui/FilterChipInput";
import LeadProspectAvatar from "../components/ui/LeadProspectAvatar";
import { ownerSelector } from "../store/store";
import {
  fetchImportDailyQuota,
  fetchLists,
  fetchUserOwnersWithWorkspaces,
  resolveDashboardScope,
} from "../utils";
import {
  addProspectSearchToList,
  cancelProspectSearch,
  createProspectSearch,
  createSavedAudience,
  deleteSavedAudience,
  detectSalesNavAccess,
  duplicateSavedAudience,
  emptyTarget,
  getProspectSearch,
  getProspectSearchResults,
  listProspectSearches,
  listSavedAudiences,
  parseProspectTarget,
  startProspectSearch,
  updateSavedAudience,
  type DiscoveredProspect,
  type ProspectSearchJob,
  type ProspectTarget,
  type SavedAudience,
  type SearchSource,
} from "../utils/prospectDiscovery";

type HubTab = "new" | "searches" | "audiences";
type WizardStep =
  | "account"
  | "describe"
  | "review"
  | "progress"
  | "results"
  | "done";

type AccountOption = {
  _id: string;
  name: string;
  avatar?: string;
  isDisconnected?: boolean;
};

/** Matches backend prospect discovery clamp. */
const MAX_DESIRED_COUNT = 1000;

const COMPANY_SIZE_OPTIONS = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "5001-10000",
  "10001+",
];

const SENIORITY_OPTIONS = [
  "Owner",
  "Partner",
  "CXO",
  "VP",
  "Director",
  "Manager",
  "Senior",
  "Entry",
  "Trainee",
  "Specialist",
];

const FUNCTION_OPTIONS = [
  "Sales",
  "Marketing",
  "Engineering",
  "Operations",
  "Finance",
  "Human Resources",
  "Information Technology",
  "Product",
  "Consulting",
  "Accounting",
];

const chipInputClass =
  "mt-1 w-full rounded-md border border-light-grey px-3 py-2 text-sm text-dark-black outline-none focus:border-mainColor";

/** Finalize filters before search / save — trim and drop empties. */
const normalizeList = (items: string[]) =>
  (items || []).map((s) => String(s || "").trim()).filter(Boolean);

const normalizeTargetForSubmit = (t: ProspectTarget): ProspectTarget => ({
  ...t,
  jobTitles: normalizeList(t.jobTitles),
  locations: normalizeList(t.locations),
  industries: normalizeList(t.industries),
  keywords: normalizeList(t.keywords),
  companies: normalizeList(t.companies),
  companySize: normalizeList(t.companySize),
  seniority: normalizeList(t.seniority || []),
  functions: normalizeList(t.functions || []),
});

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
};

const ACTIVE_JOB_STATUSES = ["draft", "queued", "searching", "processing"];

const statusLabel = (status: string) => {
  const map: Record<string, string> = {
    draft: "Draft",
    queued: "Queued",
    searching: "Searching",
    processing: "In progress",
    completed: "Completed",
    partially_completed: "Partial",
    failed: "Failed",
    cancelled: "Cancelled",
  };
  return map[status] || status;
};

const statusBadgeClass = (status: string) => {
  if (ACTIVE_JOB_STATUSES.includes(status)) {
    return "bg-bg-blue text-brand-blue";
  }
  if (status === "completed" || status === "partially_completed") {
    return "bg-success-bg text-success";
  }
  if (status === "failed") return "bg-red-50 text-red-700";
  if (status === "cancelled") return "bg-bg-grey text-muted";
  return "bg-background-grey text-dark-grey";
};

const connectionLabel = (value?: string) => {
  const s = String(value || "").trim().toUpperCase();
  if (!s || s === "NOT_STARTED" || s === "NONE" || s === "UNKNOWN") return "—";
  if (s === "ACCEPTED" || s === "CONNECTED") return "Connected";
  if (s === "PENDING") return "Pending";
  return String(value);
};

/** Human-readable filter rows from a job target (for progress/results). */
const audienceFilterRows = (
  t?: ProspectTarget | null,
  fallbackSummary?: string
): Array<{ label: string; values: string[] }> => {
  if (!t) {
    return fallbackSummary
      ? [{ label: "Audience", values: [fallbackSummary] }]
      : [];
  }
  const rows: Array<{ label: string; values: string[] }> = [
    { label: "Roles", values: normalizeList(t.jobTitles) },
    { label: "Locations", values: normalizeList(t.locations) },
    { label: "Industries", values: normalizeList(t.industries) },
    { label: "Keywords", values: normalizeList(t.keywords) },
    { label: "Companies", values: normalizeList(t.companies) },
    { label: "Company size", values: normalizeList(t.companySize) },
    { label: "Seniority", values: normalizeList(t.seniority || []) },
    { label: "Function", values: normalizeList(t.functions || []) },
  ].filter((row) => row.values.length > 0);
  if (!rows.length && fallbackSummary) {
    return [{ label: "Audience", values: [fallbackSummary] }];
  }
  return rows;
};

const FindProspects = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, owner, workspace } = useSelector(ownerSelector) as any;
  const { resolvedUserId: userId, resolvedWorkspaceId: workspaceId } =
    resolveDashboardScope(currentUser?.userId, workspace?._id);

  const initialTab = (searchParams.get("tab") as HubTab) || "new";
  const [hubTab, setHubTab] = useState<HubTab>(
    ["new", "searches", "audiences"].includes(initialTab) ? initialTab : "new"
  );

  const [step, setStep] = useState<WizardStep>("account");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState<ProspectTarget>(emptyTarget());
  const [isParsing, setIsParsing] = useState(false);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [ownerId, setOwnerId] = useState("");
  const [desiredCount, setDesiredCount] = useState("100");
  const [importDailyQuota, setImportDailyQuota] = useState<{
    limit: number;
    used: number;
    remaining: number;
    isLinkedinPremium: boolean;
  } | null>(null);
  const [searchSource, setSearchSource] = useState<SearchSource>("classic");
  const [salesNavAvailable, setSalesNavAvailable] = useState(false);
  const [savedAudienceId, setSavedAudienceId] = useState<string | undefined>();
  const [audienceNameDraft, setAudienceNameDraft] = useState("");
  const [isSavingAudience, setIsSavingAudience] = useState(false);

  const [job, setJob] = useState<ProspectSearchJob | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [prospects, setProspects] = useState<DiscoveredProspect[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lists, setLists] = useState<Array<{ _id: string; listName?: string }>>([]);
  const [saveListId, setSaveListId] = useState("");
  const [listSaveMode, setListSaveMode] = useState<"new" | "existing">("new");
  const [newListName, setNewListName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isOpeningJob, setIsOpeningJob] = useState(false);
  const [savedMeta, setSavedMeta] = useState<{ listId: string; added: number } | null>(
    null
  );
  /** Set when the search account disconnects mid-run (restriction / session). */
  const [searchAccountDisconnected, setSearchAccountDisconnected] = useState(false);

  const [historyJobs, setHistoryJobs] = useState<ProspectSearchJob[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [audiences, setAudiences] = useState<SavedAudience[]>([]);
  const [audiencesLoading, setAudiencesLoading] = useState(false);

  const setTab = (tab: HubTab) => {
    setHubTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const loadAccounts = useCallback(async (): Promise<AccountOption[]> => {
    if (!userId && !workspaceId) return [];
    try {
      const data = await fetchUserOwnersWithWorkspaces(
        1,
        50,
        "",
        userId,
        workspaceId
      );
      const rows = Array.isArray(data?.accounts) ? data.accounts : [];
      // Only connected accounts can search — hide disconnected ones from the dropdown.
      const mapped: AccountOption[] = rows
        .map((row: any) => ({
          _id: String(row._id || row.ownerId || ""),
          name: String(row.name || row.publicIdentifier || "LinkedIn account"),
          avatar: String(
            row.picture ||
              row.profileImage ||
              row.profilePic ||
              row.profilePicture ||
              ""
          ).trim(),
          isDisconnected: Boolean(row.isDisconnected),
        }))
        .filter(
          (row: AccountOption) => Boolean(row._id) && !row.isDisconnected
        );
      setAccounts(mapped);
      const preferred =
        (owner?._id && mapped.some((a) => a._id === String(owner._id))
          ? String(owner._id)
          : "") ||
        mapped[0]?._id ||
        "";
      setOwnerId((prev) => {
        if (prev && mapped.some((a) => a._id === prev)) {
          return prev;
        }
        return preferred;
      });
      return mapped;
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [owner?._id, userId, workspaceId]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (!userId && !workspaceId && !owner?._id) return;
    void (async () => {
      try {
        const data = await fetchLists(owner?._id, workspaceId, userId);
        const rows = Array.isArray(data) ? data : [];
        setLists(
          rows
            .filter((l: any) => l?._id && String(l.listName || "") !== "All Prospects")
            .map((l: any) => ({ _id: String(l._id), listName: l.listName }))
        );
      } catch (e) {
        console.error(e);
      }
    })();
  }, [owner?._id, userId, workspaceId]);

  // Prefer Sales Nav automatically when the selected account has it — no user toggle.
  useEffect(() => {
    if (!ownerId) {
      setSalesNavAvailable(false);
      setSearchSource("classic");
      setImportDailyQuota(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [salesNav, quotaData] = await Promise.all([
          detectSalesNavAccess({
            ownerId,
            userId,
            workspaceId,
          }),
          fetchImportDailyQuota({
            ownerId,
            userId,
            workspaceId,
          }).catch(() => null),
        ]);
        if (cancelled) return;
        const available = Boolean(salesNav.available);
        setSalesNavAvailable(available);
        setSearchSource(available ? "sales_nav" : "classic");
        const q = quotaData?.importDailyQuota || {};
        if (quotaData) {
          setImportDailyQuota({
            limit: Number(q.limit || 0),
            used: Number(q.used || 0),
            remaining: Number(q.remaining || 0),
            isLinkedinPremium: Boolean(q.isLinkedinPremium),
          });
        } else {
          setImportDailyQuota(null);
        }
      } catch {
        if (!cancelled) {
          setSalesNavAvailable(false);
          setSearchSource("classic");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerId, userId, workspaceId]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await listProspectSearches({
        userId,
        workspaceId,
        ownerId: ownerId || undefined,
        limit: 50,
      });
      setHistoryJobs(data.jobs || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Unable to load search history");
    } finally {
      setHistoryLoading(false);
    }
  }, [ownerId, userId, workspaceId]);

  const loadAudiences = useCallback(async () => {
    setAudiencesLoading(true);
    try {
      const data = await listSavedAudiences({
        userId,
        workspaceId,
        ownerId: ownerId || undefined,
      });
      setAudiences(data.audiences || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Unable to load saved audiences");
    } finally {
      setAudiencesLoading(false);
    }
  }, [ownerId, userId, workspaceId]);

  useEffect(() => {
    if (hubTab === "searches") void loadHistory();
    if (hubTab === "audiences") void loadAudiences();
  }, [hubTab, loadAudiences, loadHistory]);

  // Keep Prospect searches status fresh while jobs are still running.
  useEffect(() => {
    if (hubTab !== "searches") return;
    const hasActive = historyJobs.some((j) =>
      ACTIVE_JOB_STATUSES.includes(String(j.status || ""))
    );
    if (!hasActive) return;
    const id = window.setInterval(() => void loadHistory(), 4000);
    return () => window.clearInterval(id);
  }, [hubTab, historyJobs, loadHistory]);

  const openResultsForJob = useCallback(
    async (jobId: string) => {
      const results = await getProspectSearchResults({
        jobId,
        userId,
        workspaceId,
        ownerId,
        limit: 100,
      });
      setJob(results.job);
      setProspects(results.prospects || []);
      setSelectedIds((results.prospects || []).map((p) => p.id));
      if (results.job?.target) {
        setTarget({ ...emptyTarget(), ...results.job.target });
      }
      if (results.job?.ownerId) setOwnerId(String(results.job.ownerId));
      if (results.job?.searchSource === "sales_nav") setSearchSource("sales_nav");
      const connected = await loadAccounts();
      const searchOwnerId = String(results.job?.ownerId || ownerId || "");
      const stillConnected = connected.some((a) => a._id === searchOwnerId);
      setSearchAccountDisconnected(
        Boolean(results.job?.accountDisconnected) ||
          (Boolean(searchOwnerId) && !stillConnected)
      );
      setHubTab("new");
      setStep("results");
      const suggestedName =
        results.job?.targetSummary ||
        String(results.job?.target?.rawDescription || "").slice(0, 60);
      if (suggestedName) {
        setNewListName((prev) => prev.trim() || suggestedName);
      }
    },
    [loadAccounts, ownerId, userId, workspaceId]
  );

  const openJob = useCallback(
    async (jobId: string) => {
      setIsOpeningJob(true);
      try {
        const { job: existing } = await getProspectSearch({
          jobId,
          userId,
          workspaceId,
          ownerId,
        });
        if (!existing) {
          toast.error("Search not found");
          return;
        }
        setJob(existing);
        if (existing.ownerId) setOwnerId(String(existing.ownerId));
        if (existing.target) setTarget({ ...emptyTarget(), ...existing.target });
        if (existing.searchSource === "sales_nav") setSearchSource("sales_nav");
        setHubTab("new");
        const next = new URLSearchParams(searchParams);
        next.set("tab", "new");
        setSearchParams(next, { replace: true });

        if (ACTIVE_JOB_STATUSES.includes(String(existing.status || ""))) {
          setProspects([]);
          setSelectedIds([]);
          setStep("progress");
          return;
        }
        await openResultsForJob(jobId);
      } catch (e: any) {
        toast.error(e?.response?.data?.error || e?.message || "Unable to open search");
      } finally {
        setIsOpeningJob(false);
      }
    },
    [openResultsForJob, ownerId, searchParams, setSearchParams, userId, workspaceId]
  );

  // Deep-link: reopen job, find-more, or load audience
  useEffect(() => {
    const jobId = searchParams.get("jobId");
    const findMore = searchParams.get("findMore");
    const audienceId = searchParams.get("audienceId");
    if (!jobId && !audienceId) return;

    let cancelled = false;
    void (async () => {
      try {
        if (jobId && findMore === "1") {
          const { job: existing } = await getProspectSearch({
            jobId,
            userId,
            workspaceId,
            ownerId,
          });
          if (cancelled || !existing) return;
          setTarget({ ...emptyTarget(), ...(existing.target || {}) });
          setDescription(existing.target?.rawDescription || "");
          if (existing.ownerId) setOwnerId(String(existing.ownerId));
          if (existing.searchSource === "sales_nav") setSearchSource("sales_nav");
          setDesiredCount(String(existing.desiredCount || 100));
          setSavedAudienceId(undefined);
          setHubTab("new");
          setStep("review");
          toast.info("Filters loaded from previous search. Adjust and run again.");
        } else if (jobId) {
          const { job: existing } = await getProspectSearch({
            jobId,
            userId,
            workspaceId,
            ownerId,
          });
          if (cancelled || !existing) return;
          setJob(existing);
          if (existing.ownerId) setOwnerId(String(existing.ownerId));
          if (existing.target) setTarget({ ...emptyTarget(), ...existing.target });
          if (existing.searchSource === "sales_nav") setSearchSource("sales_nav");
          setHubTab("new");
          if (
            ["completed", "partially_completed", "failed", "cancelled"].includes(
              existing.status
            )
          ) {
            await openResultsForJob(jobId);
          } else if (["queued", "searching", "processing"].includes(existing.status)) {
            setStep("progress");
          } else {
            setStep("account");
          }
        } else if (audienceId) {
          const data = await listSavedAudiences({
            userId,
            workspaceId,
            ownerId: ownerId || undefined,
          });
          if (cancelled) return;
          const audience = (data.audiences || []).find((a) => a.id === audienceId);
          if (!audience) {
            toast.error("Saved audience not found");
            return;
          }
          setTarget({ ...emptyTarget(), ...(audience.target || {}) });
          setDescription(audience.target?.rawDescription || audience.description || "");
          setSearchSource(audience.searchSource === "sales_nav" ? "sales_nav" : "classic");
          setSavedAudienceId(audience.id);
          setAudienceNameDraft(audience.name);
          if (audience.ownerId) setOwnerId(String(audience.ownerId));
          setHubTab("new");
          setStep("review");
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.error || e?.message || "Unable to open search");
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("jobId");
        next.delete("findMore");
        next.delete("audienceId");
        next.set("tab", "new");
        setSearchParams(next, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally once on mount / param change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("jobId"), searchParams.get("findMore"), searchParams.get("audienceId")]);

  const pollJob = useCallback(
    async (jobId: string) => {
      const { job: next } = await getProspectSearch({
        jobId,
        userId,
        workspaceId,
        ownerId,
      });
      setJob(next);
      return next;
    },
    [ownerId, userId, workspaceId]
  );

  useEffect(() => {
    if (step !== "progress" || !job?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await pollJob(job.id);
        if (cancelled) return;
        if (
          ["completed", "partially_completed", "failed", "cancelled"].includes(
            next.status
          )
        ) {
          if (next.status === "failed") {
            toast.error(next.error || next.message || "Search failed");
          } else if (next.status === "cancelled") {
            toast.info("Search cancelled");
          }

          // Refresh sending accounts so disconnect during search shows immediately.
          const connected = await loadAccounts();
          if (cancelled) return;
          const searchOwnerId = String(next.ownerId || job.ownerId || ownerId || "");
          const stillConnected = connected.some((a) => a._id === searchOwnerId);
          const disconnected =
            Boolean(next.accountDisconnected) ||
            (Boolean(searchOwnerId) && !stillConnected);
          setSearchAccountDisconnected(disconnected);
          if (disconnected) {
            toast.warn(
              "This LinkedIn account was disconnected during the search. Reconnect it from Sending Accounts to search again."
            );
          }

          const results = await getProspectSearchResults({
            jobId: job.id,
            userId,
            workspaceId,
            ownerId: searchOwnerId || ownerId,
            limit: 100,
          });
          if (cancelled) return;
          setProspects(results.prospects || []);
          setSelectedIds((results.prospects || []).map((p) => p.id));
          setStep("results");
        }
      } catch (e) {
        console.error(e);
      }
    };
    const id = window.setInterval(() => void tick(), 2500);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [job?.id, job?.ownerId, loadAccounts, ownerId, pollJob, step, userId, workspaceId]);

  const seedManualTarget = (raw: string): ProspectTarget => {
    const text = String(raw || "").trim();
    return {
      ...emptyTarget(),
      rawDescription: text,
      keywords: text ? [text] : [],
    };
  };

  const goToManualReview = (opts?: { fromAiFailure?: boolean; message?: string }) => {
    setTarget(seedManualTarget(description));
    if (opts?.fromAiFailure) {
      toast.warn(
        opts.message
          ? `${opts.message} You can edit filters manually — search still works.`
          : "AI targeting unavailable. Continue with manual filters — search still works."
      );
    }
    setStep("review");
  };

  const handleDefineManually = () => {
    goToManualReview();
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.info("Describe who you want to reach first, or use Define filters manually.");
      return;
    }
    setIsParsing(true);
    try {
      const data = await parseProspectTarget({
        description: description.trim(),
        userId,
        workspaceId,
        ownerId,
      });
      const next = {
        ...emptyTarget(),
        ...data.target,
        rawDescription: description.trim(),
      };
      setTarget(next);
      if (Array.isArray(next.unsupported) && next.unsupported.length) {
        toast.info(`Some filters aren't available: ${next.unsupported.join(", ")}`);
      }
      setStep("review");
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || "AI targeting failed";
      goToManualReview({ fromAiFailure: true, message: msg });
    } finally {
      setIsParsing(false);
    }
  };

  const handleStartSearch = async () => {
    if (!ownerId) {
      toast.error("Select a LinkedIn account to search with.");
      return;
    }
    if (searchSource === "sales_nav" && !salesNavAvailable) {
      toast.error("Sales Navigator isn't available on this account.");
      return;
    }
    let count = parseDesiredCount();
    if (count <= 0) {
      toast.error("Enter how many prospects you want to find (at least 1).");
      setStep("account");
      return;
    }
    if (importDailyQuota != null && Number(importDailyQuota.remaining || 0) <= 0) {
      toast.error(
        "Daily import limit reached. Limit resets with your daily quota — try again after reset."
      );
      setStep("account");
      return;
    }
    if (count > MAX_DESIRED_COUNT) count = MAX_DESIRED_COUNT;
    if (
      importDailyQuota != null &&
      count > Number(importDailyQuota.remaining || 0)
    ) {
      count = Math.max(1, Number(importDailyQuota.remaining || 0));
    }
    setIsStarting(true);
    setSearchAccountDisconnected(false);
    try {
      const normalizedTarget = normalizeTargetForSubmit(target);
      setTarget(normalizedTarget);
      const created = await createProspectSearch({
        target: normalizedTarget,
        ownerId,
        desiredCount: count,
        searchSource,
        savedAudienceId,
        userId,
        workspaceId,
      });
      setJob(created.job);
      await startProspectSearch({
        jobId: created.job.id,
        userId,
        workspaceId,
        ownerId,
      });
      setStep("progress");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || "Unable to start search");
    } finally {
      setIsStarting(false);
    }
  };

  const handleCancelSearch = async () => {
    if (!job?.id || isCancelling) return;
    setIsCancelling(true);
    try {
      const { job: next } = await cancelProspectSearch({
        jobId: job.id,
        userId,
        workspaceId,
        ownerId: job.ownerId || ownerId,
      });
      setJob(next);
      // Poll effect will move to results once status is cancelled.
      toast.info("Stopping search…");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || "Unable to cancel search");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSaveAudience = async () => {
    const name = audienceNameDraft.trim();
    if (!name) {
      toast.info("Enter a name to save this audience.");
      return;
    }
    setIsSavingAudience(true);
    try {
      const normalizedTarget = normalizeTargetForSubmit(target);
      setTarget(normalizedTarget);
      const payload = {
        name,
        description: normalizedTarget.rawDescription || description,
        target: normalizedTarget,
        searchSource,
        userId,
        workspaceId,
        ownerId,
      };
      // Update in place after the first save — don't create duplicates.
      if (savedAudienceId) {
        const { audience } = await updateSavedAudience({
          id: savedAudienceId,
          ...payload,
        });
        setSavedAudienceId(audience.id);
        toast.success("Audience updated");
      } else {
        const { audience } = await createSavedAudience(payload);
        setSavedAudienceId(audience.id);
        toast.success("Audience saved");
      }
      void loadAudiences();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || "Unable to save audience");
    } finally {
      setIsSavingAudience(false);
    }
  };

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)
    );
  };

  const allSelected =
    prospects.length > 0 && prospects.every((p) => selectedIds.includes(p.id));

  const handleSave = async () => {
    if (!job?.id) return;
    if (!selectedIds.length) {
      toast.info("Select at least one prospect.");
      return;
    }
    const creatingNew = listSaveMode === "new";
    if (creatingNew && !newListName.trim()) {
      toast.info("Enter a name for the new list.");
      return;
    }
    if (!creatingNew && !saveListId) {
      toast.info("Choose an existing list.");
      return;
    }
    setIsSaving(true);
    try {
      const result = await addProspectSearchToList({
        jobId: job.id,
        listId: creatingNew ? undefined : saveListId,
        prospectIds: selectedIds,
        createList: creatingNew
          ? { listName: newListName.trim(), description: target.rawDescription }
          : undefined,
        userId,
        workspaceId,
        ownerId,
      });
      setSavedMeta({ listId: result.listId, added: result.added });
      toast.success(`Added ${result.added} prospects to list`);
      setStep("done");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || "Unable to save prospects");
    } finally {
      setIsSaving(false);
    }
  };

  const startFreshSearch = () => {
    setStep("account");
    setDescription("");
    setTarget(emptyTarget());
    setJob(null);
    setProspects([]);
    setSelectedIds([]);
    setSavedMeta(null);
    setSavedAudienceId(undefined);
    setAudienceNameDraft("");
    setSearchSource("classic");
    setHubTab("new");
  };

  const applyAudience = (audience: SavedAudience) => {
    setTarget({ ...emptyTarget(), ...(audience.target || {}) });
    setDescription(audience.target?.rawDescription || audience.description || "");
    setSearchSource(audience.searchSource === "sales_nav" ? "sales_nav" : "classic");
    setSavedAudienceId(audience.id);
    setAudienceNameDraft(audience.name);
    if (audience.ownerId) setOwnerId(String(audience.ownerId));
    setTab("new");
    // Account first — same order as Import by URL.
    setStep(ownerId || audience.ownerId ? "review" : "account");
  };

  const parseDesiredCount = () =>
    Number.parseInt(String(desiredCount || "").replace(/\D/g, ""), 10) || 0;

  const maxAllowedCount = useMemo(() => {
    const remaining =
      importDailyQuota != null && Number.isFinite(importDailyQuota.remaining)
        ? Math.max(0, Number(importDailyQuota.remaining))
        : MAX_DESIRED_COUNT;
    return Math.min(MAX_DESIRED_COUNT, remaining > 0 ? remaining : MAX_DESIRED_COUNT);
  }, [importDailyQuota]);

  const continueFromAccount = () => {
    if (!ownerId) {
      toast.error(
        accounts.length === 0
          ? "Connect a LinkedIn sending account first."
          : "Select a LinkedIn account to search with."
      );
      return;
    }
    const count = parseDesiredCount();
    if (count <= 0) {
      toast.error("Enter how many prospects you want to find (at least 1).");
      return;
    }
    if (importDailyQuota != null && Number(importDailyQuota.remaining || 0) <= 0) {
      toast.error(
        "Daily import limit reached. Limit resets with your daily quota — try again after reset."
      );
      return;
    }
    if (count > MAX_DESIRED_COUNT) {
      toast.info(`Maximum is ${MAX_DESIRED_COUNT} per search. We'll use ${MAX_DESIRED_COUNT}.`);
      setDesiredCount(String(MAX_DESIRED_COUNT));
    } else if (
      importDailyQuota != null &&
      count > Number(importDailyQuota.remaining || 0)
    ) {
      const remaining = Number(importDailyQuota.remaining || 0);
      toast.info(
        `Only ${remaining} lead${remaining === 1 ? "" : "s"} remaining in today's import limit. We'll find ${remaining} now.`
      );
      setDesiredCount(String(remaining));
    }
    setStep("describe");
  };

  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        value: a._id,
        label: a.name,
        avatar: a.avatar || "",
      })),
    [accounts]
  );

  const existingListOptions = useMemo(
    () =>
      lists.map((l) => ({
        value: l._id,
        label: l.listName || "Untitled list",
      })),
    [lists]
  );

  const visibleUnsupported = useMemo(() => {
    const rows = Array.isArray(target.unsupported) ? target.unsupported : [];
    if (searchSource === "sales_nav") {
      return rows.filter(
        (label) =>
          !/seniority/i.test(label) && !/function/i.test(label)
      );
    }
    return rows;
  }, [searchSource, target.unsupported]);

  const jobAudienceRows = useMemo(
    () => audienceFilterRows(job?.target, job?.targetSummary),
    [job?.target, job?.targetSummary]
  );

  const toggleChip = (
    field: "companySize" | "seniority" | "functions",
    value: string,
    active: boolean
  ) => {
    setTarget((t) => {
      const current = Array.isArray(t[field]) ? t[field]! : [];
      return {
        ...t,
        [field]: active
          ? current.filter((s) => s !== value)
          : [...current, value],
      };
    });
  };

  return (
    <section className="noScroll h-full min-w-0 w-full overflow-y-auto overflow-x-hidden bg-background-grey">
    <div className="mx-auto w-full min-w-0 max-w-5xl px-4 py-6 sm:px-6">
      <div className="mb-6 flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            className="text-xs font-medium text-mainColor hover:underline"
            onClick={() => navigate("/leads")}
          >
            ← Back to Leads Database
          </button>
          <h1 className="mt-2 text-2xl font-semibold text-dark-black">Find Prospects</h1>
          <p className="mt-1 text-sm text-dark-grey">
            Describe your audience or set filters manually — no LinkedIn URL required.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={startFreshSearch}>
          New search
        </Button>
      </div>

      <div className="mb-5 flex flex-wrap gap-1 border-b border-light-grey">
        {(
          [
            { id: "new" as const, label: "New search" },
            { id: "searches" as const, label: "Prospect searches" },
            { id: "audiences" as const, label: "Saved audiences" },
          ] as const
        ).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setTab(tab.id)}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              hubTab === tab.id
                ? "border-mainColor text-mainColor"
                : "border-transparent text-dark-grey hover:text-dark-black"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {hubTab === "searches" ? (
        <div className="rounded-[12px] border border-light-grey bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-dark-black">Prospect searches</h2>
            <Button variant="outline" size="sm" onClick={() => void loadHistory()}>
              Refresh
            </Button>
          </div>
          {historyLoading ? (
            <p className="text-sm text-dark-grey">Loading…</p>
          ) : historyJobs.length === 0 ? (
            <p className="text-sm text-dark-grey">
              No searches yet. Start a new search to find prospects.
            </p>
          ) : (
            <div className="overflow-auto rounded-md border border-light-grey">
              <table className="w-full text-left text-xs">
                <thead className="bg-background-grey text-dark-grey">
                  <tr>
                    <th className="p-2">Audience</th>
                    <th className="p-2">Account</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Found</th>
                    <th className="p-2">When</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {historyJobs.map((row) => (
                    <tr key={row.id} className="border-t border-light-grey">
                      <td className="p-2 font-medium text-dark-black">
                        {row.targetSummary || "Untitled search"}
                      </td>
                      <td className="p-2 text-dark-grey">{row.accountName || "—"}</td>
                      <td className="p-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadgeClass(
                            row.status
                          )}`}
                        >
                          {ACTIVE_JOB_STATUSES.includes(row.status) ? "● " : ""}
                          {statusLabel(row.status)}
                        </span>
                        {ACTIVE_JOB_STATUSES.includes(row.status) ? (
                          <p className="mt-1 text-[10px] text-dark-grey">
                            {row.processed || 0} processed
                            {row.message ? ` · ${row.message}` : ""}
                          </p>
                        ) : null}
                        {row.status === "failed" && row.error ? (
                          <p className="mt-1 text-[10px] text-red-600">
                            {String(row.error).slice(0, 80)}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-2 text-dark-grey">{row.discovered || 0}</td>
                      <td className="p-2 text-dark-grey">{formatDate(row.createdAt)}</td>
                      <td className="p-2">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            isDisabled={isOpeningJob}
                            onClick={() => void openJob(row.id)}
                          >
                            {ACTIVE_JOB_STATUSES.includes(row.status)
                              ? "Resume"
                              : "Open"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const next = new URLSearchParams();
                              next.set("tab", "new");
                              next.set("jobId", row.id);
                              next.set("findMore", "1");
                              setSearchParams(next);
                            }}
                          >
                            Find more
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {hubTab === "audiences" ? (
        <div className="rounded-[12px] border border-light-grey bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-dark-black">Saved audiences</h2>
            <Button variant="outline" size="sm" onClick={() => void loadAudiences()}>
              Refresh
            </Button>
          </div>
          {audiencesLoading ? (
            <p className="text-sm text-dark-grey">Loading…</p>
          ) : audiences.length === 0 ? (
            <p className="text-sm text-dark-grey">
              No saved audiences yet. Save filters from a search to reuse them later.
            </p>
          ) : (
            <div className="space-y-3">
              {audiences.map((audience) => (
                <div
                  key={audience.id}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-light-grey p-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-dark-black">{audience.name}</p>
                    {audience.description ? (
                      <p className="mt-0.5 text-xs text-dark-grey">
                        {String(audience.description).slice(0, 100)}
                      </p>
                    ) : null}
                    <p className="mt-1 text-xs text-dark-grey">
                      Updated {formatDate(audience.updatedAt)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => applyAudience(audience)}
                    >
                      Use
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await duplicateSavedAudience({
                            id: audience.id,
                            userId,
                            workspaceId,
                            ownerId,
                          });
                          toast.success("Audience duplicated");
                          void loadAudiences();
                        } catch (e: any) {
                          toast.error(
                            e?.response?.data?.error || "Unable to duplicate"
                          );
                        }
                      }}
                    >
                      Duplicate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await deleteSavedAudience({
                            id: audience.id,
                            userId,
                            workspaceId,
                            ownerId,
                          });
                          toast.success("Audience deleted");
                          void loadAudiences();
                        } catch (e: any) {
                          toast.error(e?.response?.data?.error || "Unable to delete");
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {hubTab === "new" && step === "account" ? (
        <div className="rounded-[12px] border border-light-grey bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-dark-black">Search settings</h2>
          <p className="mt-1 text-sm text-dark-grey">
            Choose which LinkedIn account to search with, and how many prospects to find.
          </p>

          {accounts.length === 0 ? (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
              <p className="font-medium">No LinkedIn account connected</p>
              <p className="mt-1 text-xs">
                Connect or reconnect a sending account first, then come back to Find
                Prospects. Disconnected accounts are not available for search.
              </p>
              <Button
                variant="primary"
                size="sm"
                className="mt-3"
                onClick={() => navigate("/teams")}
              >
                Go to Sending Accounts
              </Button>
            </div>
          ) : (
            <div className="mt-5 max-w-md space-y-4">
              <AccountSelect
                label="LinkedIn account"
                value={ownerId}
                onChange={setOwnerId}
                placeholder="Select LinkedIn account"
                options={accountOptions}
              />
              <label className="block space-y-2 text-sm font-medium text-dark-black">
                <span>How many prospects?</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={desiredCount}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, "");
                    setDesiredCount(digitsOnly);
                  }}
                  onBlur={() => {
                    const count = parseDesiredCount();
                    if (count <= 0) return;
                    if (count > maxAllowedCount) {
                      setDesiredCount(String(maxAllowedCount));
                    }
                  }}
                  placeholder="e.g. 100"
                  className="h-9 w-full rounded-lg border border-[#E1E1E1] bg-white px-2.5 text-sm text-dark-black outline-none focus:border-brand-blue"
                  disabled={
                    importDailyQuota != null &&
                    Number(importDailyQuota.remaining || 0) <= 0
                  }
                />
              </label>
              {importDailyQuota ? (
                <p className="text-xs text-dark-grey">
                  Daily import limit:{" "}
                  <span className="font-semibold text-dark-black">
                    {Number(importDailyQuota.used || 0)} /{" "}
                    {Number(importDailyQuota.limit || 0)}
                  </span>
                  {Number(importDailyQuota.remaining || 0) <= 0
                    ? " · Limit reached — resets with daily quota"
                    : ` · ${Number(importDailyQuota.remaining || 0)} remaining today`}
                  {importDailyQuota.isLinkedinPremium ? " · Premium" : ""}
                  {" · "}Max {MAX_DESIRED_COUNT} per search
                </p>
              ) : (
                <p className="text-xs text-dark-grey">
                  Enter a number from 1 to {MAX_DESIRED_COUNT}.
                </p>
              )}
              {ownerId && salesNavAvailable ? (
                <p className="text-xs text-dark-grey">
                  This account has Sales Navigator — we’ll use richer targeting filters when
                  available.
                </p>
              ) : null}
            </div>
          )}

          <div className="mt-6 flex flex-wrap justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/leads")}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={continueFromAccount}
              isDisabled={
                accounts.length === 0 ||
                (importDailyQuota != null &&
                  Number(importDailyQuota.remaining || 0) <= 0)
              }
            >
              Continue
            </Button>
          </div>
        </div>
      ) : null}

      {hubTab === "new" && step === "describe" ? (
        <div className="rounded-[12px] border border-light-grey bg-white p-5 shadow-sm">
          <label className="text-sm font-semibold text-dark-black">
            Who do you want to reach?
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            placeholder="Founders, owners and CEOs of dental and aesthetic clinics in Riyadh and Jeddah with 5–100 employees."
            className={`${chipInputClass} mt-2 min-h-[140px] resize-y`}
          />
          <p className="mt-2 text-xs text-dark-grey">
            AI targeting is optional. You can define filters manually at no AI cost.
          </p>
          <div className="mt-4 flex flex-wrap justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setStep("account")}>
              Back
            </Button>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDefineManually}
                isDisabled={isParsing}
              >
                Define filters manually
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleGenerate()}
                isDisabled={isParsing}
              >
                {isParsing ? "Generating…" : "Generate Targeting"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {hubTab === "new" && step === "review" ? (
        <div className="w-full min-w-0 max-w-full overflow-hidden rounded-[12px] border border-light-grey bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-dark-black">Your Target Audience</h2>
          <p className="mt-1 text-xs text-dark-grey">
            Review and edit filters before searching. LinkedIn search does not use AI.
          </p>

          <p className="mt-3 rounded-md border border-light-grey bg-background-grey px-3 py-2 text-[11px] text-dark-grey">
            Add each value as a tag: type the text, then press <strong>Enter</strong> or{" "}
            <strong>comma</strong>. Remove a tag only by clicking its <strong>×</strong>.
          </p>

          <div className="mt-4 grid min-w-0 gap-3 sm:grid-cols-2">
            <FilterChipInput
              label="Roles / Job titles"
              values={target.jobTitles}
              onChange={(jobTitles) => setTarget((t) => ({ ...t, jobTitles }))}
              placeholder="e.g. Founder"
            />
            <FilterChipInput
              label="Locations"
              values={target.locations}
              onChange={(locations) => setTarget((t) => ({ ...t, locations }))}
              placeholder="e.g. Riyadh"
            />
            <FilterChipInput
              label="Industries"
              values={target.industries}
              onChange={(industries) => setTarget((t) => ({ ...t, industries }))}
              placeholder="e.g. Medical Practice"
            />
            <FilterChipInput
              label="Keywords"
              values={target.keywords}
              onChange={(keywords) => setTarget((t) => ({ ...t, keywords }))}
              placeholder="e.g. Dental"
            />
            <FilterChipInput
              label="Companies (optional)"
              values={target.companies}
              onChange={(companies) => setTarget((t) => ({ ...t, companies }))}
              placeholder="e.g. Acme Corp"
              className="min-w-0 sm:col-span-2"
            />
          </div>

          <div className="mt-4 min-w-0">
            <p className="text-xs font-semibold text-dark-grey">Company size</p>
            <div className="mt-2 flex w-full min-w-0 flex-wrap gap-2">
              {COMPANY_SIZE_OPTIONS.map((size) => {
                const active = (target.companySize || []).includes(size);
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleChip("companySize", size, active)}
                    className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${
                      active
                        ? "border-mainColor bg-mainColor text-white"
                        : "border-light-grey bg-white text-dark-grey"
                    }`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </div>

          {searchSource === "sales_nav" ? (
            <>
              <div className="mt-4 min-w-0">
                <p className="text-xs font-semibold text-dark-grey">Seniority</p>
                <div className="mt-2 flex w-full min-w-0 flex-wrap gap-2">
                  {SENIORITY_OPTIONS.map((item) => {
                    const active = (target.seniority || []).includes(item);
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => toggleChip("seniority", item, active)}
                        className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${
                          active
                            ? "border-mainColor bg-mainColor text-white"
                            : "border-light-grey bg-white text-dark-grey"
                        }`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-4 min-w-0">
                <p className="text-xs font-semibold text-dark-grey">Function</p>
                <div className="mt-2 flex w-full min-w-0 flex-wrap gap-2">
                  {FUNCTION_OPTIONS.map((item) => {
                    const active = (target.functions || []).includes(item);
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => toggleChip("functions", item, active)}
                        className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${
                          active
                            ? "border-mainColor bg-mainColor text-white"
                            : "border-light-grey bg-white text-dark-grey"
                        }`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          {visibleUnsupported.length > 0 ? (
            <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Not available with this search mode: {visibleUnsupported.join(", ")}
            </p>
          ) : null}

          <div className="mt-5 rounded-md border border-light-grey bg-background-grey p-3">
            <p className="text-xs font-semibold text-dark-black">Save as audience</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                className="h-8 w-full max-w-xs rounded-md border border-light-grey px-2.5 text-xs text-dark-black outline-none focus:border-mainColor"
                value={audienceNameDraft}
                onChange={(e) => setAudienceNameDraft(e.target.value)}
                placeholder="e.g. Saudi dental clinic owners"
              />
              <Button
                variant="outline"
                size="sm"
                className="!h-8 !px-2.5 !text-xs"
                onClick={() => void handleSaveAudience()}
                isDisabled={isSavingAudience}
              >
                {isSavingAudience
                  ? "Saving…"
                  : savedAudienceId
                    ? "Update audience"
                    : "Save audience"}
              </Button>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setStep("describe")}>
              Back
            </Button>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep("account")}>
                Change account
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleStartSearch()}
                isDisabled={isStarting}
              >
                {isStarting ? "Starting…" : "Find Prospects"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {hubTab === "new" && step === "progress" ? (
        <div className="rounded-[12px] border border-light-grey bg-white p-8 text-center shadow-sm">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusBadgeClass(
              job?.status || "processing"
            )}`}
          >
            ● {statusLabel(job?.status || "processing")}
          </span>
          <p className="mt-3 text-lg font-semibold text-dark-black">Finding prospects…</p>
          <p className="mt-2 text-sm text-dark-grey">{job?.message || "Starting search"}</p>
          <p className="mx-auto mt-2 max-w-md text-xs text-dark-grey">
            This runs in the background. You can leave this page — reopen it anytime from{" "}
            <button
              type="button"
              className="font-medium text-mainColor hover:underline"
              onClick={() => setTab("searches")}
            >
              Prospect searches
            </button>
            .
          </p>
          <div className="mx-auto mt-6 grid max-w-md grid-cols-3 gap-3 text-sm">
            <div className="rounded-md bg-background-grey p-3">
              <p className="text-xs text-dark-grey">Discovered</p>
              <p className="text-lg font-semibold text-mainColor">{job?.discovered || 0}</p>
            </div>
            <div className="rounded-md bg-background-grey p-3">
              <p className="text-xs text-dark-grey">Processed</p>
              <p className="text-lg font-semibold text-mainColor">{job?.processed || 0}</p>
            </div>
            <div className="rounded-md bg-background-grey p-3">
              <p className="text-xs text-dark-grey">Already in your leads</p>
              <p className="text-lg font-semibold text-mainColor">{job?.duplicates || 0}</p>
            </div>
          </div>
          {Number(job?.desiredCount || 0) > 0 ? (
            <p className="mt-4 text-xs text-dark-grey">
              Looking for {job?.desiredCount} prospects
            </p>
          ) : null}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleCancelSearch()}
              isDisabled={isCancelling}
            >
              {isCancelling ? "Cancelling…" : "Cancel search"}
            </Button>
          </div>
          {jobAudienceRows.length > 0 ? (
            <div className="mx-auto mt-5 max-w-lg rounded-md border border-light-grey bg-background-grey px-4 py-3 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-dark-grey">
                Searching for
              </p>
              <dl className="mt-2 space-y-2">
                {jobAudienceRows.map((row) => (
                  <div key={row.label} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <dt className="shrink-0 text-xs font-medium text-dark-grey">
                      {row.label}:
                    </dt>
                    <dd className="flex flex-wrap gap-1">
                      {row.values.map((value) => (
                        <span
                          key={`${row.label}-${value}`}
                          className="rounded-full border border-light-grey bg-white px-2 py-0.5 text-[11px] text-dark-black"
                        >
                          {value}
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
              {job?.searchSource === "sales_nav" ? (
                <p className="mt-2 text-[10px] text-dark-grey">Via Sales Navigator</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {hubTab === "new" && step === "results" ? (
        <div className="rounded-[12px] border border-light-grey bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-dark-black">Prospect results</h2>
                {job?.status ? (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadgeClass(
                      job.status
                    )}`}
                  >
                    {statusLabel(job.status)}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-dark-grey">
                {prospects.length} found · {selectedIds.length} selected
                {job?.status === "failed" && job?.error
                  ? ` · ${job.error}`
                  : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStep("review");
                  setProspects([]);
                  setSelectedIds([]);
                  setJob(null);
                }}
              >
                Find more
              </Button>
              <label className="flex items-center gap-2 text-xs text-dark-grey">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) =>
                    setSelectedIds(e.target.checked ? prospects.map((p) => p.id) : [])
                  }
                />
                Select all
              </label>
            </div>
          </div>

          {job?.status === "failed" ? (
            <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              This search failed{job.error ? `: ${job.error}` : "."} You can still save any
              prospects that were found, or start a new search.
            </p>
          ) : null}

          {searchAccountDisconnected || job?.accountDisconnected ? (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              This LinkedIn account was disconnected during the search (for example a
              temporary restriction). Reconnect it from{" "}
              <button
                type="button"
                className="font-semibold underline"
                onClick={() => navigate("/teams")}
              >
                Sending Accounts
              </button>{" "}
              before starting another search.
            </p>
          ) : null}

          {jobAudienceRows.length > 0 ? (
            <div className="mt-3 rounded-md border border-light-grey bg-background-grey px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-dark-grey">
                Searched for
              </p>
              <dl className="mt-2 space-y-1.5">
                {jobAudienceRows.map((row) => (
                  <div key={row.label} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <dt className="shrink-0 text-xs font-medium text-dark-grey">
                      {row.label}:
                    </dt>
                    <dd className="flex flex-wrap gap-1">
                      {row.values.map((value) => (
                        <span
                          key={`${row.label}-${value}`}
                          className="rounded-full border border-light-grey bg-white px-2 py-0.5 text-[11px] text-dark-black"
                        >
                          {value}
                        </span>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
              {job?.searchSource === "sales_nav" ? (
                <p className="mt-1.5 text-[10px] text-dark-grey">Via Sales Navigator</p>
              ) : null}
            </div>
          ) : null}

          <div className="mt-4 max-h-[420px] overflow-auto rounded-md border border-light-grey">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-background-grey text-dark-grey">
                <tr>
                  <th className="p-2" />
                  <th className="p-2">Prospect</th>
                  <th className="p-2">Title</th>
                  <th className="p-2">Company</th>
                  <th className="p-2">Location</th>
                  <th className="p-2">Connection</th>
                </tr>
              </thead>
              <tbody>
                {prospects.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-dark-grey">
                      {job?.status === "failed"
                        ? "No prospects were saved before this search failed."
                        : "No prospects found for this target."}
                    </td>
                  </tr>
                ) : (
                  prospects.map((p) => (
                    <tr key={p.id} className="border-t border-light-grey">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(p.id)}
                          onChange={(e) => toggleSelected(p.id, e.target.checked)}
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <LeadProspectAvatar
                            picture={p.imageUrl}
                            initials={initials(p.fullName)}
                          />
                          <span className="font-medium text-dark-black">{p.fullName}</span>
                        </div>
                      </td>
                      <td className="p-2 text-dark-grey">{p.title || "—"}</td>
                      <td className="p-2 text-dark-grey">{p.company || "—"}</td>
                      <td className="p-2 text-dark-grey">{p.location || "—"}</td>
                      <td className="p-2 text-dark-grey">
                        {connectionLabel(p.connected)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-5 rounded-md border border-light-grey bg-background-grey p-4">
            <p className="text-sm font-semibold text-dark-black">Save to a list</p>
            <p className="mt-0.5 text-xs text-dark-grey">
              Choose where to keep the selected prospects.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setListSaveMode("new");
                  setSaveListId("");
                }}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                  listSaveMode === "new"
                    ? "border-mainColor bg-mainColor text-white"
                    : "border-light-grey bg-white text-dark-grey"
                }`}
              >
                Create new list
              </button>
              <button
                type="button"
                onClick={() => {
                  setListSaveMode("existing");
                  if (!saveListId && existingListOptions[0]?.value) {
                    setSaveListId(existingListOptions[0].value);
                  }
                }}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                  listSaveMode === "existing"
                    ? "border-mainColor bg-mainColor text-white"
                    : "border-light-grey bg-white text-dark-grey"
                }`}
              >
                Add to existing list
              </button>
            </div>
            <div className="mt-3 max-w-md space-y-1.5">
              {listSaveMode === "new" ? (
                <label className="block space-y-1.5 text-xs font-semibold text-dark-grey">
                  <span>New list name</span>
                  <input
                    className="h-9 w-full rounded-lg border border-[#E1E1E1] bg-white px-2.5 text-sm font-normal text-dark-black outline-none focus:border-brand-blue"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="e.g. Laravel developers — Islamabad"
                  />
                </label>
              ) : (
                <Select
                  label="Existing list"
                  variant="default"
                  size="sm"
                  placeholder="Select a list"
                  value={saveListId}
                  onChange={setSaveListId}
                  options={
                    existingListOptions.length
                      ? existingListOptions
                      : [{ value: "", label: "No lists yet — create a new one" }]
                  }
                  className="!shadow-none"
                />
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSave()}
                isDisabled={isSaving || prospects.length === 0}
              >
                {isSaving
                  ? "Saving…"
                  : `Save ${selectedIds.length || 0} Prospect${selectedIds.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {hubTab === "new" && step === "done" && savedMeta ? (
        <div className="rounded-[12px] border border-light-grey bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-dark-black">
            {savedMeta.added} prospects added
          </p>
          <p className="mt-2 text-sm text-dark-grey">
            Ready to open the list or start a campaign.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/prospects/${savedMeta.listId}`)}
            >
              View List
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                navigate(
                  `/campaign-management/create?listId=${encodeURIComponent(savedMeta.listId)}`
                )
              }
            >
              Create Campaign
            </Button>
            <Button variant="outline" size="sm" onClick={startFreshSearch}>
              Find more prospects
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/leads")}>
              Back to Leads
            </Button>
          </div>
        </div>
      ) : null}
    </div>
    </section>
  );
};

export default FindProspects;
